import { ArrowDown, ArrowUp, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { PriceDisplay } from "@/components/ui/price-display";
import { cn } from "@/lib/utils";

import type { DiscountCampaignAnalyticsShape } from "../../types/discount-analytics-types";

interface Props {
  campaign: DiscountCampaignAnalyticsShape;
}

export function HaloServicesChart({ campaign }: Props) {
  const { t } = useTranslation("reports");

  const halo = campaign.haloServices;
  const hasData = halo.length > 0;
  const maxRevenue = hasData ? Math.max(...halo.map((h) => h.revenueAttributed)) : 1;

  const liftPercent = (campaign.upsellLift * 100).toFixed(0);
  const liftPositive = campaign.upsellLift > 0;
  const hasLiftData =
    campaign.avgServicesPerAppointmentBaseline > 0 ||
    campaign.avgServicesPerAppointmentDiscounted > 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-amber-500" />
              {t("discounts.halo.title")}
            </CardTitle>
            <CardDescription>{t("discounts.halo.description")}</CardDescription>
          </div>
          {hasLiftData && (
            <Badge
              variant={liftPositive ? "default" : "destructive"}
              className="flex items-center gap-1"
            >
              {liftPositive ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
              {t("discounts.halo.upsellLift", { percent: liftPercent })}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {hasLiftData && (
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="rounded-md border bg-background p-2">
              <div className="text-muted-foreground">
                {t("discounts.halo.servicesPerAppointmentDiscounted")}
              </div>
              <div className="mt-1 font-semibold text-base tabular-nums">
                {campaign.avgServicesPerAppointmentDiscounted.toFixed(2)}
              </div>
            </div>
            <div className="rounded-md border bg-background p-2">
              <div className="text-muted-foreground">
                {t("discounts.halo.servicesPerAppointmentBaseline")}
              </div>
              <div className="mt-1 font-semibold text-base tabular-nums">
                {campaign.avgServicesPerAppointmentBaseline.toFixed(2)}
              </div>
            </div>
          </div>
        )}

        {hasData ? (
          <div className="space-y-2">
            <div className="text-xs text-muted-foreground">{t("discounts.halo.topServices")}</div>
            {halo.map((service) => {
              const widthPercent = (service.revenueAttributed / maxRevenue) * 100;
              return (
                <div key={service.serviceId} className="space-y-1">
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <span className="font-medium truncate">{service.serviceName}</span>
                    <span className="text-muted-foreground tabular-nums shrink-0">
                      <PriceDisplay amount={service.revenueAttributed} />
                    </span>
                  </div>
                  <div className="relative h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className={cn(
                        "absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-amber-300 to-amber-500"
                      )}
                      style={{ width: `${widthPercent}%` }}
                    />
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    {t("discounts.halo.attachRate", {
                      percent: (service.attachRate * 100).toFixed(0),
                      count: service.appointmentCount,
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-xs text-muted-foreground italic">
            {t("discounts.halo.emptyState")}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
